/**
 * 📦 Página de Existencias (Stock por Local)
 * ListView + useListViews + useStandardListFilters. StatsSnapshotBar (datos en tiempo real) en contentAboveCard; vistas Todos / Bajo / Crítico.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Card,
  Spinner,
  Text,
  InlineStack,
  Box,
  EmptyState,
  BlockStack,
  Modal,
  Banner,
  ChoiceList,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { ProductIcon } from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { useAuth, usePagination } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { ROUTES, routeHelpers, storePath, slugifyProductName } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getStatsPageCache, setStatsPageCache, listViewCacheKey } from '@/lib/statsPageCache';
import {
  fetchAllActiveProductsForStock,
  fetchInventoryQuantities,
  sumQuantitiesByProduct,
} from '@/lib/inventoryApi';
import { getCachedBranches, setCachedBranches } from '@/lib/prefetchStatsPages';
import { StatsSnapshotBar } from '@/components/admin/StatsSnapshotBar';
import { getExistenciasListConfig } from '@/config/listViews';
import type { ExistenciasListItem } from '@/config/listViews';
import { useListViews } from '@/hooks/useListViews';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';

const LIST_ID = 'existencias';

const EXISTENCIAS_SYSTEM_VIEWS = [
  { id: 'all', label: 'Todos', isSystem: true, query: '', filterValues: { stock: [] as string[] } },
  { id: 'low', label: 'Bajo (6-10)', isSystem: true, query: '', filterValues: { stock: ['low'] } },
  { id: 'critical', label: 'Crítico (≤5)', isSystem: true, query: '', filterValues: { stock: ['critical'] } },
];

const STOCK_FILTER_OPTIONS = [
  { label: 'Todos', value: 'all' },
  { label: 'Bajo (6-10)', value: 'low' },
  { label: 'Crítico (≤5)', value: 'critical' },
] as const;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'stock_desc', label: 'Stock mayor primero' },
  { value: 'stock_asc', label: 'Stock menor primero' },
  { value: 'category_asc', label: 'Categoría A-Z' },
  { value: 'category_desc', label: 'Categoría Z-A' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

interface Product {
  id: string;
  name: string;
  category: string;
  image_url?: string;
  stock?: number; // Stock desde existencias para el branch seleccionado
  slug?: string; // Se usa para la URL del detalle de inventario
}

interface Branch {
  id: string;
  name: string;
}

function filterByStockAndSearch(
  products: Product[],
  stockFilter: 'all' | 'low' | 'critical',
  search: string
): Product[] {
  let filtered = products;
  if (stockFilter === 'low') {
    filtered = filtered.filter((p) => p.stock != null && p.stock <= 10 && p.stock > 5);
  } else if (stockFilter === 'critical') {
    filtered = filtered.filter((p) => p.stock != null && p.stock <= 5);
  }
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter((p) => p.name.toLowerCase().includes(q));
  }
  return filtered;
}

function sortExistencias(items: ExistenciasListItem[], sortOption: string): ExistenciasListItem[] {
  const list = [...items];
  const [key, dir] = sortOption.includes('_') ? sortOption.split('_') : [sortOption, 'asc'];
  if (key === 'name') {
    list.sort((a, b) => {
      const c = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return dir === 'desc' ? -c : c;
    });
  } else if (key === 'stock') {
    list.sort((a, b) => (dir === 'desc' ? b.stock - a.stock : a.stock - b.stock));
  } else if (key === 'category') {
    list.sort((a, b) => {
      const c = (a.category ?? '').localeCompare(b.category ?? '', undefined, { sensitivity: 'base' });
      return dir === 'desc' ? -c : c;
    });
  }
  return list;
}

export default function ExistenciasPage() {
  const navigate = useNavigate();
  const { storeSlug } = useParams<{ storeSlug?: string }>();
  const { tenantId, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  /** [] = Todos; ['sin_local'] = Sin local; [branchId, ...] = uno o más locales (agregar stock) */
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({ stock: [] });
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [showBulkZeroConfirm, setShowBulkZeroConfirm] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const toast = useAdminToast();

  /** Solo un local concreto seleccionado (para actualizar stock) */
  const isBranchSelected = selectedBranchIds.length === 1 && selectedBranchIds[0] !== 'sin_local';
  const singleBranchId = isBranchSelected ? selectedBranchIds[0]! : null;

  // Cargar branches cuando haya tenant; si hay caché (prefetch), aplicarla al instante y revalidar en background
  useEffect(() => {
    if (!tenantId) {
      setBranchesLoading(false);
      return;
    }
    loadBranches();
  }, [tenantId]);

  const loadBranches = async () => {
    if (!tenantId) return;
    const cached = getCachedBranches(tenantId);
    if (cached?.length) {
      setBranches(cached);
      setBranchesLoading(false);
    } else {
      setBranchesLoading(true);
    }
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      const list = (data ?? []) as Branch[];
      setBranches(list);
      setCachedBranches(tenantId, list);
      if (list.length === 1) setSelectedBranchIds([list[0].id]);
    } catch (error: unknown) {
      toast.error('Error al cargar locales', getErrorMessage(error));
    } finally {
      setBranchesLoading(false);
    }
  };

  /** [] = Todos; ['sin_local'] = Sin local; [id, ...] = agregar stock de esos locales */
  const loadProducts = useCallback(async (revalidateOnly = false) => {
    if (!tenantId) return;
    const branchKey = [...selectedBranchIds].sort().join(',');
    const cacheKey = listViewCacheKey('existencias', `${tenantId}|${branchKey}`);
    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ items: Product[] }>(cacheKey);
      if (cached?.items !== undefined) {
        setProducts(cached.items);
        setLoading(false);
        loadProducts(true).catch(() => {});
        return;
      }
    }
    if (!revalidateOnly) setLoading(true);
    try {
      // Paginado: antes ambas queries truncaban en 1000 (PostgREST) → lista incompleta y,
      // peor, totales de stock MAL (inventory tiene una fila por producto × sucursal).
      const productsData = await fetchAllActiveProductsForStock(tenantId);

      const productIds = productsData.map((p) => p.id);
      const isSinLocal = selectedBranchIds.length === 1 && selectedBranchIds[0] === 'sin_local';
      const branchIds = selectedBranchIds.filter((id) => id !== 'all' && id !== 'sin_local');

      // Si el inventario falla, LANZA (lo agarra el catch y avisa). Antes se tragaba el
      // error y TODOS los productos caían a `product.stock` → cifras falsas sin aviso.
      const invRows = await fetchInventoryQuantities(productIds, branchIds.length > 0 ? branchIds : null);
      const byProductId = sumQuantitiesByProduct(invRows);

      let productsWithStock: Product[] = productsData.map((p) => ({
        id: p.id,
        name: p.name,
        category: p.category ?? '',
        image_url: p.image_url ?? undefined,
        slug: p.slug ?? undefined,
        // Sin fila de inventario para ese producto → se usa el stock a nivel producto.
        stock: byProductId[p.id] ?? (p.stock ?? 0),
      }));

      if (branchIds.length === 0 && isSinLocal) {
        productsWithStock = productsWithStock.filter((p) => (p.stock ?? 0) === 0);
      }

      setProducts(productsWithStock);
      setStatsPageCache(cacheKey, { items: productsWithStock });
    } catch (error: unknown) {
      toast.error('Error al cargar productos', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedBranchIds, toast]);

  useLayoutEffect(() => {
    if (!authLoading && tenantId) {
      loadProducts();
    }
  }, [authLoading, tenantId, selectedBranchIds, loadProducts]);

  useEffect(() => {
    if (selectedBranchIds.length === 0) setLoading(false);
  }, [selectedBranchIds]);

  const filtersRecord = useMemo(() => ({ stock: filters.stock ?? [] }), [filters.stock]);
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilters(f);
  }, []);

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: EXISTENCIAS_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { handleSwitchView } = views;

  const toUrl = useCallback(
    (path: string) => navigate(storeSlug ? storePath(storeSlug, path) : path),
    [navigate, storeSlug]
  );
  const listConfig = useMemo(() => getExistenciasListConfig(toUrl), [toUrl]);

  const stockFilter = (filters.stock?.[0] as 'all' | 'low' | 'critical') ?? 'all';
  const filteredProducts = useMemo(
    () => filterByStockAndSearch(products, stockFilter, searchValue),
    [products, stockFilter, searchValue]
  );

  const clearFilters = useCallback(() => {
    setSearchValue('');
    setFilters({ stock: [] });
    setSelectedBranchIds([]);
    handleSwitchView(0);
  }, [handleSwitchView]);

  const appliedFiltersList = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    const stock = filters.stock ?? [];
    const hasStockFilter = stock.length > 0 && stock[0] !== 'all';
    if (hasStockFilter) {
      const label = STOCK_FILTER_OPTIONS.find((o) => o.value === stock[0])?.label ?? stock[0];
      items.push({
        key: 'stock',
        label: `Estado de stock: ${label}`,
        onRemove: () => setFilters((prev) => ({ ...prev, stock: [] })),
      });
    }
    if (selectedBranchIds.length > 0) {
      const label =
        selectedBranchIds[0] === 'sin_local'
          ? 'Sin local'
          : selectedBranchIds.map((id) => branches.find((b) => b.id === id)?.name ?? id).join(', ');
      items.push({
        key: 'branch',
        label: `Local: ${label}`,
        onRemove: () => setSelectedBranchIds([]),
      });
    }
    return items;
  }, [filters.stock, selectedBranchIds, branches]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'stock',
        label: 'Estado de stock',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Estado de stock"
            titleHidden
            choices={STOCK_FILTER_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            selected={filters.stock?.length ? filters.stock : ['all']}
            onChange={(v) =>
              setFilters((prev) => ({ ...prev, stock: v && v[0] === 'all' ? [] : v ?? [] }))
            }
            allowMultiple={false}
          />
        ),
      },
      {
        key: 'branch',
        label: 'Local',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Local"
            titleHidden
            choices={[
              { label: 'Todos', value: 'all' },
              { label: 'Sin local', value: 'sin_local' },
              ...branches.map((b) => ({ label: b.name, value: b.id })),
            ]}
            selected={selectedBranchIds.length === 0 ? ['all'] : selectedBranchIds}
            onChange={(v) => {
              if (!v?.length || v.includes('all')) setSelectedBranchIds([]);
              else if (v.length === 1 && v[0] === 'sin_local') setSelectedBranchIds(['sin_local']);
              else setSelectedBranchIds(v.filter((x) => x !== 'all' && x !== 'sin_local'));
            }}
            allowMultiple={true}
          />
        ),
      },
    ],
    [filters.stock, branches, selectedBranchIds]
  );

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config: listConfig,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: 'Buscar productos…',
    filters: indexFiltersFilters,
    appliedFilters: appliedFiltersList,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(/\s/g, '_')),
    mode,
    setMode,
  });

  const listItemsFull = useMemo(() => {
    const base: ExistenciasListItem[] = filteredProducts.map((p) => ({
      id: p.id,
      name: p.name,
      image_url: p.image_url && (p.image_url.startsWith('http') || p.image_url.startsWith('/')) ? p.image_url : '/placeholder.svg',
      category: p.category || '',
      stock: p.stock ?? 0,
      slug: (p as { slug?: string }).slug ?? slugifyProductName(p.name),
    }));
    return sortExistencias(base, sortOption);
  }, [filteredProducts, sortOption]);

  const pagination = usePagination();
  useEffect(() => {
    pagination.setTotal(listItemsFull.length);
    if (listItemsFull.length > 0 && pagination.offset >= listItemsFull.length) {
      pagination.goToFirstPage();
    }
  }, [listItemsFull.length, pagination]);

  const listItems = useMemo(
    () => listItemsFull.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [listItemsFull, pagination.offset, pagination.pageSize]
  );

  const paginationOption = buildListViewPagination(pagination);

  const handleRowEdit = useCallback(
    (item: ExistenciasListItem) => {
      const path = routeHelpers.productInventoryDetail(item.slug);
      const fullPath = storeSlug ? storePath(storeSlug, path) : path;
      navigate(fullPath, { state: { branchId: singleBranchId ?? undefined } });
    },
    [navigate, storeSlug, singleBranchId]
  );

  const handleBulkSetZeroClick = useCallback(() => {
    if (selectedProductIds.length === 0) {
      toast.error('Selecciona al menos un producto');
      return;
    }
    setShowBulkZeroConfirm(true);
  }, [selectedProductIds.length, toast]);

  const handleBulkAction = useCallback(
    (actionKey: string, ids: string[]) => {
      if (actionKey === 'setZero') handleBulkSetZeroClick();
      if (actionKey === 'export' && ids.length > 0) {
        const items = listItemsFull.filter((x) => ids.includes(x.id));
        exportListToExcel({
          columns: listConfig.columns,
          items: items.map((i) => ({ ...i })),
          filename: getExportFilename(listConfig.title),
        });
        toast.success('Exportado', `${items.length} producto(s) exportado(s) correctamente.`);
      }
    },
    [listItemsFull, listConfig, toast, handleBulkSetZeroClick]
  );


  const handleConfirmBulkSetZero = useCallback(async () => {
    if (!singleBranchId || selectedProductIds.length === 0) {
      if (selectedProductIds.length > 0 && !singleBranchId) {
        toast.error('Selecciona un solo local en el filtro para actualizar el stock');
      }
      return;
    }
    const branchId = singleBranchId;
    setBulkActionLoading(true);
    try {
      for (const productId of selectedProductIds) {
        // Los errores de acá NO se pueden tragar: es stock (plata). Antes se ignoraban los
        // tres y la acción reportaba éxito igual — con el stock sin cambiar y/o el asiento
        // de auditoría mal o ausente.
        const { data: current, error: readErr } = await supabase
          .from('inventory')
          .select('id, quantity')
          .eq('branch_id', branchId)
          .eq('product_id', productId)
          .maybeSingle();
        if (readErr) throw readErr; // sin esto, oldQty caía a 0 y el quantity_change quedaba mal

        const oldQty = current?.quantity ?? 0;
        let inventoryId = current?.id;

        const { error: upsertErr } = await supabase
          .from('inventory')
          .upsert(
            {
              branch_id: branchId,
              product_id: productId,
              quantity: 0,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'branch_id,product_id' }
          );
        if (upsertErr) throw upsertErr; // sin esto, se avisaba "listo" sin haber puesto el stock en 0

        if (inventoryId == null) {
          const { data: inserted, error: findErr } = await supabase
            .from('inventory')
            .select('id')
            .eq('branch_id', branchId)
            .eq('product_id', productId)
            .single();
          if (findErr) throw findErr;
          inventoryId = inserted?.id;
        }

        if (inventoryId && oldQty !== 0) {
          const { error: ledgerErr } = await supabase.from('inventory_transactions').insert({
            inventory_id: inventoryId,
            transaction_type: 'adjustment',
            quantity_change: -oldQty,
            notes: `Ajuste masivo: stock a 0 (antes ${oldQty})`,
          });
          if (ledgerErr) throw ledgerErr; // stock cambiado sin asiento = auditoría inconsistente
        }
      }
      toast.success(
        selectedProductIds.length === 1
          ? 'Stock actualizado a 0'
          : `${selectedProductIds.length} productos con stock en 0`
      );
      setShowBulkZeroConfirm(false);
      setSelectedProductIds([]);
      await loadProducts();
    } catch (error: unknown) {
      toast.error('Error al actualizar stock en masa', getErrorMessage(error));
    } finally {
      setBulkActionLoading(false);
    }
  }, [singleBranchId, selectedProductIds, loadProducts, toast]);

  const lowStockProducts = products.filter((p) => p.stock && p.stock <= 10 && p.stock > 5);
  const criticalProducts = products.filter((p) => p.stock && p.stock <= 5);

  const barStats = useMemo(() => {
    const count = products.length;
    const units = products.reduce((s, p) => s + (p.stock ?? 0), 0);
    const low = lowStockProducts.length;
    const critical = criticalProducts.length;
    const outOfStock = products.filter((p) => (p.stock ?? 0) === 0).length;
    const avgPerProduct = count > 0 ? units / count : 0;
    return { count, units, low, critical, outOfStock, avgPerProduct };
  }, [products, lowStockProducts.length, criticalProducts.length]);

  // Spinner solo mientras cargan los locales (no mientras cargan productos sin local seleccionado)
  if (branchesLoading) {
    return (
      <Page
        title={
          <PageBreadcrumbTitle
            icon={ProductIcon}
            title="Existencias"
            onBack={() => toUrl(ROUTES.PRODUCTS)}
            backAccessibilityLabel="Volver a Productos"
          />
        }
      >
        <Card>
          <Box padding="800">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  // Sin locales: mensaje único. Con locales pero sin selección: se muestra la página con filtros para que el usuario elija.
  if (branches.length === 0) {
    return (
      <Page
        title={
          <PageBreadcrumbTitle
            icon={ProductIcon}
            title="Existencias"
            onBack={() => toUrl(ROUTES.PRODUCTS)}
            backAccessibilityLabel="Volver a Productos"
          />
        }
      >
        <Card>
          <EmptyState
            heading="No hay locales disponibles"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <Text as="p" variant="bodyMd">Necesitas crear al menos un local para gestionar existencias.</Text>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const contentAboveCard = (
    <StatsSnapshotBar
      items={[
        { label: 'Productos', value: barStats.count },
        { label: 'Unidades', value: barStats.units },
        { label: 'Stock bajo', value: barStats.low, tone: 'caution' },
        { label: 'Crítico', value: barStats.critical, tone: 'critical' },
        { label: 'Sin stock', value: barStats.outOfStock, tone: 'critical' },
      ]}
    />
  );

  const contentBefore = filterControl;

  const pageTitle = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title="Existencias"
      onBack={() => toUrl(ROUTES.PRODUCTS)}
      backAccessibilityLabel="Volver a Productos"
    />
  );

  return (
    <>
      <ListView<ExistenciasListItem>
        config={listConfig}
        items={listItems}
        loading={loading && products.length === 0}
        pagination={paginationOption}
        pageTitle={pageTitle}
        contentAboveCard={contentAboveCard}
        contentAboveCardInCard={false}
        contentBefore={contentBefore}
        contentBeforeInsideCard
        selectedIds={selectedProductIds}
        onSelectionChange={setSelectedProductIds}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onRowEdit={handleRowEdit}
        onBulkAction={handleBulkAction}
      />
      <Modal
          open={showBulkZeroConfirm}
          onClose={() => !bulkActionLoading && setShowBulkZeroConfirm(false)}
          title="Establecer stock a 0"
          primaryAction={{
            content: bulkActionLoading ? 'Actualizando…' : 'Establecer a 0',
            onAction: handleConfirmBulkSetZero,
            loading: bulkActionLoading,
          }}
          secondaryActions={[
            {
              content: 'Cancelar',
              onAction: () => setShowBulkZeroConfirm(false),
              disabled: bulkActionLoading,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Banner tone="warning">
                <Text as="p">
                  ¿Poner stock en 0 para {selectedProductIds.length} producto{selectedProductIds.length !== 1 ? 's' : ''} seleccionado{selectedProductIds.length !== 1 ? 's' : ''} en este local? Esta acción actualizará el inventario.
                </Text>
              </Banner>
            </BlockStack>
          </Modal.Section>
        </Modal>
    </>
  );
}
